import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { FC } from 'react';
import useMeasure from 'react-use-measure';
import { Icon } from 'src/components-v2/DataDisplay';
import {
  DesignSystemFontFamilyMap,
  getResponsiveCssFontStyles,
  resolveDesignSystemFontStyle,
} from 'src/lib/design-system/typography';
import type {
  DecorativeTextShape,
  EmbeddedInlineEntry,
} from 'src/types/contentful';
import styled from 'threads5/styles/styled';

const fontWeightMap: Record<DecorativeTextShape['fontWeight'], number> = {
  Regular: 400,
  'Semi-Bold': 600,
  Bold: 700,
  'Extra Bold': 800,
  Light: 300,
};

const StyledContainer = styled('span', {
  shouldForwardProp: (prop: string) => {
    return !['display', 'color', 'fontSize'].includes(prop);
  },
})<{
  display?: DecorativeTextShape['display'];
  color?: string;
  fontSize?: DecorativeTextShape['fontSize'];
}>(({ display, color, fontSize, theme }) => {
  return {
    color: color ? theme.palette[color] : 'inherit',
    columnGap: theme.spacing(1),
    display: display === 'Inline' ? 'inline-flex' : 'flex',
    flexDirection: 'row',
    ...(fontSize &&
      getResponsiveCssFontStyles(
        theme,
        resolveDesignSystemFontStyle(fontSize),
      )),
  };
});

const StyledSpan = styled('span', {
  shouldForwardProp: (prop: string) => {
    return !['gradient', 'font', 'fontWeight'].includes(prop);
  },
})<{
  gradient?: string;
  fontWeight?: DecorativeTextShape['fontWeight'];
  font?: DecorativeTextShape['font'];
}>(({ gradient, theme, font, fontWeight }) => {
  return {
    ...(gradient &&
      theme.palette[`gradient/${gradient}`] && {
        background: theme.palette[`gradient/${gradient}`],
        backgroundClip: 'text',
        color: 'transparent',
        // With `background-clip: text`, glyph parts outside the painted box render
        // transparent: negative letter-spacing pushes glyphs past the horizontal
        // edges, and the stat styles' sub-100% line-heights push caps/descenders
        // past the vertical ones. Padding extends the paint area on both axes;
        // the negative margin cancels the layout impact.
        // Placing this within the gradient conditional helps prevent regressions in existing usages
        // where padding or margin (but not both) might be manually overriden in the CMS.
        padding: '8px',
        margin: '-8px',
      }),
    fontFamily: font ? DesignSystemFontFamilyMap[font] : undefined, // TODO: should pull from the MUI theme.ts?
    fontWeight: fontWeight ? fontWeightMap[fontWeight] : undefined,
    flex: 1,
    display: 'inline-block',
  };
});

const getBackgroundImage = (fields, refBounds) => {
  if (fields?.backgroundImage) {
    return {
      '&::before': {
        backgroundImage: `url('${fields?.backgroundImage?.fields?.file?.url.replace(
          /\?(.+)/,
          '?fm=webp&q=1', // we don't set a width since we do not know the size of the uploaded image
        )}')`,
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'contain',
        content: '""',
        display: 'block',
        height: refBounds.height,
        position: 'absolute',
        width: refBounds.width,
        zIndex: 0,
      },
    };
  }
};

const DecorativeText: FC<{
  node: EmbeddedInlineEntry<DecorativeTextShape>;
}> = ({
  node: {
    data: {
      target: { fields, sys },
    },
  },
}) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys.id,
  });
  const [ref, refBounds] = useMeasure();

  return (
    <StyledContainer
      className='decorative-text'
      display={fields.display}
      color={
        fields.color?.fields && 'color' in fields.color.fields
          ? fields.color.fields.color
          : undefined
      }
      sx={fields.styles}
      fontSize={fields.fontSize}
      {...inspectorProps({
        fieldId: 'text',
      })}
    >
      {fields?.icon && (
        <Icon
          icon={fields?.icon?.fields?.icon}
          sx={{ color: 'inherit!important' }}
        />
      )}
      <StyledSpan
        className='decorative-text-content'
        ref={ref}
        gradient={
          fields.color?.fields && 'gradient' in fields.color.fields
            ? fields.color.fields.gradient
            : undefined
        }
        fontWeight={fields.fontWeight}
        font={fields.font}
        sx={getBackgroundImage(fields, refBounds)}
      >
        {fields.text}
      </StyledSpan>
    </StyledContainer>
  );
};
DecorativeText.displayName = 'DecorativeText';

export default DecorativeText;
